import React, { useEffect, useState } from "react";
import SuperAdminLayout from "./SuperAdminLayout";
import {
    SectionHeader,
    CardGlass,
    Button,
    Badge,
    StatCard
} from "@/components/ui/PremiumComponents";
import {
    Truck,
    DollarSign,
    Navigation,
    Zap,
    Save,
    ArrowLeft,
    Percent,
    Calendar,
    Settings,
    ShieldAlert
} from 'lucide-react';
import { toast } from "sonner";
import axios from "axios";
import { getSAToken } from "@/services/superadminApi";

const _raw = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BASE_URL = _raw.replace(/\/api\/?$/, "");

export default function SuperAdminDeliverySettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({
        default_delivery_base_price: 2.50,
        default_delivery_price_per_km: 1.00,
        default_delivery_radius_km: 10.0,
        default_platform_fee_model: "commission",
        default_platform_commission_rate: 0.05,
        default_platform_monthly_fee: 49.0,
        default_rider_pay_model: "per_order",
        default_rider_pay_per_order: 3.00,
        default_rider_pay_per_km: 0.50,
        default_glovo_commission: 0.30,
        default_ubereats_commission: 0.30,
        default_justeat_commission: 0.30,
        sla_alerts_enabled: false,
        sla_pending_max_min: 15,
        sla_assigned_max_min: 10,
        sla_picked_up_max_min: 30
    });

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const token = getSAToken();
            const res = await axios.get(`${BASE_URL}/api/superadmin/settings/delivery`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setConfig(res.data);
        } catch (err) {
            toast.error("Error al cargar configuración de delivery");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = getSAToken();
            await axios.patch(`${BASE_URL}/api/superadmin/settings/delivery`, config, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Configuración global de delivery actualizada");
        } catch (err) {
            toast.error("Error al guardar cambios");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <SuperAdminLayout><div className="flex items-center justify-center min-h-[60vh]"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div></SuperAdminLayout>;

    return (
        <SuperAdminLayout>
            <div className="page-fade-in pb-24">
                <div className="mb-8">
                    <button
                        onClick={() => window.history.back()}
                        className="flex items-center gap-2 text-sm font-bold transition-colors mb-4"
                        style={{ color: 'var(--text-soft)' }}
                    >
                        <ArrowLeft size={16} /> Volver a Ajustes
                    </button>
                    <SectionHeader
                        title="Configuración Global de Delivery"
                        subtitle="Define los parámetros por defecto para todos los restaurantes de la red Conexo."
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Tarificación Clientes */}
                    <CardGlass title="Costes para el Cliente" icon={<DollarSign size={20} className="text-primary" />}>
                        <div className="mt-6 space-y-6">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>Base Fija Delivery (€)</label>
                                <input
                                    type="number"
                                    value={config.default_delivery_base_price}
                                    onChange={e => setConfig({ ...config, default_delivery_base_price: parseFloat(e.target.value) })}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                                />
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>Precio Variable (€/KM)</label>
                                <input
                                    type="number"
                                    value={config.default_delivery_price_per_km}
                                    onChange={e => setConfig({ ...config, default_delivery_price_per_km: parseFloat(e.target.value) })}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                                />
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>Radio Máximo (KM)</label>
                                <div className="flex items-center gap-4">
                                    <input
                                        type="range"
                                        min="1" max="50"
                                        value={config.default_delivery_radius_km}
                                        onChange={e => setConfig({ ...config, default_delivery_radius_km: parseFloat(e.target.value) })}
                                        className="flex-1 accent-primary"
                                    />
                                    <span className="text-xl font-black text-white w-12">{config.default_delivery_radius_km}</span>
                                </div>
                            </div>
                        </div>
                    </CardGlass>

                    {/* Ganancias Riders */}
                    <CardGlass title="Pago a Repartidores" icon={<Truck size={20} className="text-secondary" />}>
                        <div className="mt-6 space-y-6">
                            <div className="flex gap-2 p-1 bg-white/5 border border-white/10 rounded-2xl mb-4">
                                {['per_order', 'per_km', 'mixed'].map(m => (
                                    <button
                                        key={m}
                                        onClick={() => setConfig({ ...config, default_rider_pay_model: m })}
                                        className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${config.default_rider_pay_model === m ? 'bg-primary text-white shadow-lg' : 'text-soft hover:bg-white/5'}`}
                                    >
                                        {m === 'per_order' ? 'Fijo' : m === 'per_km' ? 'Distancia' : 'Mixto'}
                                    </button>
                                ))}
                            </div>

                            {(config.default_rider_pay_model === 'per_order' || config.default_rider_pay_model === 'mixed') && (
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>Pago Base por Pedido (€)</label>
                                    <input
                                        type="number"
                                        value={config.default_rider_pay_per_order}
                                        onChange={e => setConfig({ ...config, default_rider_pay_per_order: parseFloat(e.target.value) })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                                    />
                                </div>
                            )}

                            {(config.default_rider_pay_model === 'per_km' || config.default_rider_pay_model === 'mixed') && (
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>Plus por Kilómetro (€)</label>
                                    <input
                                        type="number"
                                        value={config.default_rider_pay_per_km}
                                        onChange={e => setConfig({ ...config, default_rider_pay_per_km: parseFloat(e.target.value) })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                                    />
                                </div>
                            )}
                        </div>
                    </CardGlass>

                    {/* Modelo SaaS */}
                    <CardGlass title="Modelo Conexo SaaS" icon={<Zap size={20} className="text-accent" />}>
                        <div className="mt-6 space-y-6">
                            <div className="flex gap-2 p-1 bg-white/5 border border-white/10 rounded-2xl">
                                {['commission', 'monthly'].map(m => (
                                    <button
                                        key={m}
                                        onClick={() => setConfig({ ...config, default_platform_fee_model: m })}
                                        className={`flex-1 py-3 px-4 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${config.default_platform_fee_model === m ? 'bg-accent text-white shadow-lg' : 'text-soft hover:bg-white/5'}`}
                                    >
                                        {m === 'commission' ? 'Comisión' : 'Cuota Fija'}
                                    </button>
                                ))}
                            </div>

                            {config.default_platform_fee_model === 'commission' ? (
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>% Comisión Conexo</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={config.default_platform_commission_rate * 100}
                                            onChange={e => setConfig({ ...config, default_platform_commission_rate: parseFloat(e.target.value) / 100 })}
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-accent/20"
                                        />
                                        <Percent size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-accent" />
                                    </div>
                                    <p className="text-[10px] font-medium" style={{ color: 'var(--text-soft)' }}>
                                        Se aplica sobre el coste total de envío que paga el cliente.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>SaaS Fee Mensual (€)</label>
                                    <input
                                        type="number"
                                        value={config.default_platform_monthly_fee}
                                        onChange={e => setConfig({ ...config, default_platform_monthly_fee: parseFloat(e.target.value) })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-accent/20"
                                    />
                                    <p className="text-[10px] font-medium" style={{ color: 'var(--text-soft)' }}>
                                        Cargo recurrente al restaurante por usar el módulo delivery sin límites.
                                    </p>
                                </div>
                            )}
                        </div>
                    </CardGlass>

                    {/* Alertas SLA */}
                    <CardGlass title="Motor Alertas SLA" icon={<ShieldAlert size={20} className="text-warning" />}>
                        <div className="mt-6 space-y-6">
                            <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl">
                                <div>
                                    <h4 className="text-sm font-black text-white">Activar SLA Global</h4>
                                    <p className="text-[10px]" style={{ color: 'var(--text-soft)' }}>Si un pedido excede el tiempo, genera alerta de demora.</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={config.sla_alerts_enabled}
                                        onChange={e => setConfig({ ...config, sla_alerts_enabled: e.target.checked })}
                                    />
                                    <div className="w-11 h-6 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-warning"></div>
                                </label>
                            </div>

                            <div className={`space-y-4 transition-opacity ${!config.sla_alerts_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-warning/80">
                                        Max tiempo Pending (min)
                                    </label>
                                    <input
                                        type="number"
                                        value={config.sla_pending_max_min}
                                        onChange={e => setConfig({ ...config, sla_pending_max_min: parseInt(e.target.value) })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-warning/20"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-warning/80">
                                        Max tiempo Assigned (min)
                                    </label>
                                    <input
                                        type="number"
                                        value={config.sla_assigned_max_min}
                                        onChange={e => setConfig({ ...config, sla_assigned_max_min: parseInt(e.target.value) })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-warning/20"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-warning/80">
                                        Max tiempo Picked Up (min)
                                    </label>
                                    <input
                                        type="number"
                                        value={config.sla_picked_up_max_min}
                                        onChange={e => setConfig({ ...config, sla_picked_up_max_min: parseInt(e.target.value) })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-warning/20"
                                    />
                                </div>
                            </div>
                        </div>
                    </CardGlass>

                    {/* Plataformas Externas */}
                    <CardGlass title="Comisiones Plataformas (Default)" icon={<Globe size={20} className="text-secondary" />}>
                        <div className="mt-6 space-y-6">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>Glovo (%)</label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={(config.default_glovo_commission * 100).toFixed(2)}
                                        onChange={e => setConfig({ ...config, default_glovo_commission: parseFloat(e.target.value) / 100 })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                                    />
                                    <Percent size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-primary" />
                                </div>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>Uber Eats (%)</label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={(config.default_ubereats_commission * 100).toFixed(2)}
                                        onChange={e => setConfig({ ...config, default_ubereats_commission: parseFloat(e.target.value) / 100 })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                                    />
                                    <Percent size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-primary" />
                                </div>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: 'var(--text-soft)' }}>Just Eat (%)</label>
                                <div className="relative">
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={(config.default_justeat_commission * 100).toFixed(2)}
                                        onChange={e => setConfig({ ...config, default_justeat_commission: parseFloat(e.target.value) / 100 })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:ring-2 ring-primary/20"
                                    />
                                    <Percent size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-primary" />
                                </div>
                            </div>
                        </div>
                    </CardGlass>
                </div>

                {/* Resumen Informativo */}
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <CardGlass title="Simulación de Ticket (Promedio 3.5km)" className="bg-primary/5">
                        <div className="space-y-4 font-mono text-sm tracking-tight pt-4">
                            <div className="flex justify-between border-b border-white/5 pb-2">
                                <span style={{ color: 'var(--text-soft)' }}>Coste Envío Cliente:</span>
                                <span className="text-white font-bold">{(config.default_delivery_base_price + (3.5 * config.default_delivery_price_per_km)).toFixed(2)}€</span>
                            </div>
                            <div className="flex justify-between border-b border-white/5 pb-2">
                                <span style={{ color: 'var(--text-soft)' }}>Ganancia Repartidor:</span>
                                <span className="text-success font-bold">
                                    {config.default_rider_pay_model === 'per_order' ? config.default_rider_pay_per_order.toFixed(2) :
                                        config.default_rider_pay_model === 'per_km' ? (3.5 * config.default_rider_pay_per_km).toFixed(2) :
                                            (config.default_rider_pay_per_order + (3.5 * config.default_rider_pay_per_km)).toFixed(2)}€
                                </span>
                            </div>
                            <div className="flex justify-between font-black text-accent text-base border-t border-white/10 pt-4">
                                <span>Ingreso Neto Conexo (Fee):</span>
                                <span>
                                    {config.default_platform_fee_model === 'commission' ?
                                        ((config.default_delivery_base_price + (3.5 * config.default_delivery_price_per_km)) * config.default_platform_commission_rate).toFixed(2) :
                                        "VARIABLE"}€
                                </span>
                            </div>
                        </div>
                    </CardGlass>

                    <CardGlass title="Impacto en Ventas" className="bg-white/5">
                        <div className="grid grid-cols-2 gap-4">
                            <StatCard label="Pedidos/Mes Estim." value="1.2k" icon={<Navigation size={18} />} color="var(--primary)" />
                            <StatCard label="Ahorro vs Comis. Ext." value="24%" icon={<Percent size={18} />} color="var(--success)" />
                        </div>
                    </CardGlass>
                </div>

                {/* Global Sticky Save Button */}
                <div className="fixed bottom-10 right-10 z-50">
                    <Button
                        variant="primary"
                        className="flex items-center gap-3 px-8 py-5 rounded-3xl shadow-2xl scale-110"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <Save size={20} />
                        )}
                        <span className="text-base">Guardar Cambios Globales</span>
                    </Button>
                </div>
            </div>
        </SuperAdminLayout>
    );
}
